import httpClient from 'hub-http/clients/apiClient';
import { createRpcClientV2, readUnhandledEnumCase } from 'rpc-client-utils';
import { getUpgradeConfigWithPurchaseMotionsRpc } from '../../__generated__/chirp/com/hubspot/upgrade/management/rpc/UpgradeConfigInAppRpc';
import { getIsHubSpotOnCreditsPilot } from '../_core/common/eligibility/getIsHubSpotOnCreditsPilot';
import { logRavenException } from '../_core/utils/errors';
import { mapProductForHubSpotOnCredits } from '../_core/utils/mapProductForHubSpotOnCredits';
const rpcClient = createRpcClientV2({
  hubHttpClient: httpClient
});
export let UpgradePointStatus;
(function (UpgradePointStatus) {
  UpgradePointStatus["STALE"] = "STALE";
  UpgradePointStatus["ACTIVE"] = "ACTIVE";
  UpgradePointStatus["ARCHIVED"] = "ARCHIVED";
  UpgradePointStatus["PLANNED"] = "PLANNED";
})(UpgradePointStatus || (UpgradePointStatus = {}));
const upgradePointPromises = {};

// viewedUpgradePointsCache is a module cache that stores whether a user has
// viewed an upgrade point, via a unique upgradeKey. This allows to us to
// prevent usage trackers from firing more than once per app session.
export const viewedUpgradePointsCache = {};
export const updateViewedUpgradePointsCache = (upgradeKey, onCacheUpdate) => {
  if (!viewedUpgradePointsCache[upgradeKey]) {
    viewedUpgradePointsCache[upgradeKey] = true;
    if (onCacheUpdate) onCacheUpdate();
  }
};
export function getApp() {
  return window.location.pathname.split('/')[1];
}
export function getScreen() {
  return window.location.pathname;
}
export function getHubAndTierFromApiName(apiName) {
  const hub = apiName.split('-')[0];
  const tier = apiName.split('-')[2];
  return {
    hub: hub === 'crm' ? 'suite' : hub,
    tier: !tier && hub === 'hubspot' ? 'free' : tier // free tools api name has no tier
  };
}
export function isCommerceHub(hub) {
  return hub === 'commerce';
}
export function fetchUpgradePoint({
  upgradeKey,
  shouldIncludePurchaseMotions = false,
  limit,
  amountUsed
}) {
  if (!upgradeKey || upgradeKey === '') {
    return Promise.resolve();
  }
  return Promise.all([rpcClient.call(getUpgradeConfigWithPurchaseMotionsRpc, {
    request: {
      upgradeKey,
      shouldIncludePurchaseMotions,
      limit,
      amountUsed
    }
  }), getIsHubSpotOnCreditsPilot()]).then(([response, isInPilot]) => {
    const upgradeConfig = response.upgradeConfigProduct;
    if (isInPilot && upgradeConfig) {
      return mapProductForHubSpotOnCredits(upgradeConfig);
    }
    return upgradeConfig;
  }).catch(error => {
    var _error$cause;
    const message = ((_error$cause = error.cause) === null || _error$cause === void 0 ? void 0 : _error$cause.status) === 404 ? `${upgradeKey}: upgrade point may not exist in the UMS.` : `${upgradeKey}: unexpected error fetching upgrade config`;
    logRavenException({
      customMessage: message,
      error,
      uniqueId: upgradeKey,
      fingerprint: ['fetchUpgradePoint']
    });
    return undefined;
  });
}
export function getUpgradePointPromise({
  upgradeKey
}) {
  if (upgradePointPromises[upgradeKey]) {
    return upgradePointPromises[upgradeKey];
  }
  const upgradePointPromise = new Promise(resolve => {
    resolve(fetchUpgradePoint({
      upgradeKey,
      shouldIncludePurchaseMotions: false
    }));
  });
  upgradePointPromises[upgradeKey] = upgradePointPromise;
  return upgradePointPromise;
}
export const getRepName = (repInfo = {}) => {
  if (repInfo && repInfo.name) {
    return repInfo.name;
  }
  return 'Not assigned';
};
export function getUpgradeConfigTrackingProperties(upgradeConfig) {
  return {
    app: getApp(),
    screen: getScreen(),
    feature: upgradeConfig.featureStoreFeature,
    upgradeKey: upgradeConfig.upgradeKey,
    pointType: upgradeConfig.pointType.toString()
    //we intentionally don't track upgradeProduct and apiName at the level where we fetch the upgrade config.
  };
}
export const getSource = (app, screen, uniqueId) => `${app}-${screen}-general-${uniqueId}`;
export function getTrackingProperties(upgradeConfig) {
  const app = getApp();
  const screen = getScreen();
  const source = getSource(app, screen, upgradeConfig.upgradeKey);
  const {
    hub,
    tier
  } = getHubAndTierFromApiName(upgradeConfig.bestFitApiNameForPurchase);
  return {
    app,
    screen,
    uniqueId: upgradeConfig.upgradeKey,
    apiName: upgradeConfig.bestFitApiNameForPurchase,
    source,
    upgradeKey: upgradeConfig.upgradeKey,
    pointType: readUnhandledEnumCase(upgradeConfig.pointType),
    feature: upgradeConfig.featureStoreFeature,
    hub,
    tier
  };
}
export const warnArchivedUpgradePointUse = upgradeKey => {
  console.warn(`Upgrade point '${upgradeKey}' is archived, and should not be used in-app. If this upgrade point should not be archived, it can be restored through the UMS at https://tools.hubteamqa.com/ums/upgrade-points/${upgradeKey}.`);
};

/**
 * Unit and integration testing only
 */
function resetUpgradePointCaches() {
  Object.keys(upgradePointPromises).forEach(upgradeKey => {
    delete upgradePointPromises[upgradeKey];
  });
  Object.keys(viewedUpgradePointsCache).forEach(upgradeKey => {
    delete viewedUpgradePointsCache[upgradeKey];
  });
}
export const __TEST_UTILS__ = {
  resetUpgradePointCaches
};